import { useState, useCallback, useRef, useEffect } from 'react';
import gsap from 'gsap';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, hexToRgba, resolveImage } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

const MAX_VISIBLE = 4;

function StarRating({ rating = 5, accent }) {
    const stars = Math.max(0, Math.min(5, Math.round(rating)));
    return (
        <div className="flex items-center gap-0.5 mb-3">
            {Array.from({ length: 5 }, (_, i) => (
                <svg key={i} className="w-4 h-4" viewBox="0 0 20 20" fill={i < stars ? accent : 'none'} stroke={i < stars ? accent : '#d1d5db'} strokeWidth={1.5}>
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            ))}
        </div>
    );
}

function Avatar({ src, name, accent }) {
    if (src) return <img src={src} alt={name || ''} className="w-10 h-10 rounded-full object-cover border-2" style={{ borderColor: hexToRgba(accent, 0.3) }} loading="lazy" />;
    const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
    return <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0" style={{ backgroundColor: accent }}>{initials}</div>;
}

function CardContent({ item, accent, s }) {
    return (
        <>
            <StarRating rating={item.rating} accent={accent} />
            {item.quote && <p className="text-base leading-relaxed mb-5 italic" style={{ color: s.text }}>&ldquo;{item.quote}&rdquo;</p>}
            <div className="flex items-center gap-3">
                <Avatar src={item.avatar_url} name={item.author} accent={accent} />
                <div>
                    {item.author && <p className="font-semibold text-sm" style={{ color: s.text }}>{item.author}</p>}
                    {(item.role || item.company) && <p className="text-xs" style={{ color: s.textSub }}>{[item.role, item.company].filter(Boolean).join(' · ')}</p>}
                </div>
            </div>
        </>
    );
}

function Dots({ items, activeIndex, accent, onGo }) {
    return (
        <div className="flex justify-center gap-2 mt-6">
            {items.map((_, i) => (
                <button key={i} onClick={() => onGo(i)} className="w-2 h-2 rounded-full transition-all duration-300"
                    style={{ backgroundColor: i === activeIndex ? accent : hexToRgba(accent, 0.25), transform: i === activeIndex ? 'scale(1.4)' : 'scale(1)' }}
                    aria-label={`Testimonio ${i + 1}`} />
            ))}
        </div>
    );
}

export default function TestimonialStackSection({ block = {}, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'stack';
    const items = (content.items || []).map(item => {
        const resolved = resolveImage(item, 'avatar', mediaFolders);
        return resolved ? { ...item, avatar_url: resolved } : item;
    });
    const [activeIndex, setActiveIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const advance = useCallback(() => setActiveIndex(p => (p + 1) % items.length), [items.length]);
    const goTo = useCallback((i) => { setActiveIndex(i); setPaused(true); }, []);

    // Autoplay
    const interval = config.autoplay_interval ? Math.max(2, Math.min(30, config.autoplay_interval)) : 0;
    useEffect(() => {
        if (!interval || paused || items.length <= 1) return;
        const timer = gsap.delayedCall(interval, () => {
            advance();
        });
        return () => timer.kill();
    }, [activeIndex, interval, paused, items.length, advance]);
    // Resume autoplay after 10s of inactivity
    useEffect(() => {
        if (!paused || !interval) return;
        const t = setTimeout(() => setPaused(false), 10000);
        return () => clearTimeout(t);
    }, [paused, interval]);

    // Measure first card for dynamic height
    const measureRef = useRef(null);
    const [cardH, setCardH] = useState(280);
    useEffect(() => {
        if (!measureRef.current) return;
        const h = measureRef.current.offsetHeight;
        if (h > 50) setCardH(h);
    }, [items, activeIndex]);

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#f9fafb', ...s.bgStyle };
    const accent = s.accent;
    const cardBg = s.dark ? '#1e293b' : '#ffffff';
    const cardBorder = hexToRgba(accent, 0.12);
    const cardShadow = `0 4px 24px ${hexToRgba(accent, 0.1)}, 0 1px 4px rgba(0,0,0,0.06)`;

    // ── Stack: cards apiladas, click para avanzar ──
    if (variant === 'stack') {
        const ordered = Array.from({ length: items.length }, (_, i) => (activeIndex + i) % items.length);
        const visibleStack = ordered.slice(0, MAX_VISIBLE);
        const stackPadding = (visibleStack.length - 1) * 12;

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="max-w-2xl mx-auto">
                        <div className="relative" style={{ height: cardH + stackPadding }}>
                            {visibleStack.slice().reverse().map((itemIdx, stackPos) => {
                                const depth = visibleStack.length - 1 - stackPos;
                                const isTop = depth === 0;
                                return (
                                    <div key={itemIdx}
                                        ref={isTop ? measureRef : undefined}
                                        className="absolute left-0 right-0 rounded-2xl p-6 md:p-8 cursor-pointer select-none"
                                        style={{
                                            top: depth * 12,
                                            backgroundColor: cardBg,
                                            border: `1px solid ${cardBorder}`,
                                            boxShadow: isTop ? cardShadow : `0 2px 8px rgba(0,0,0,0.04)`,
                                            transform: `scale(${1 - depth * 0.04})`,
                                            opacity: depth === 0 ? 1 : depth === 1 ? 0.7 : depth === 2 ? 0.45 : 0.25,
                                            zIndex: MAX_VISIBLE - depth,
                                            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            pointerEvents: isTop ? 'auto' : 'none',
                                        }}
                                        onClick={isTop ? advance : undefined}>
                                        <CardContent item={items[itemIdx]} accent={accent} s={s} />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex items-center justify-between mt-4 px-1">
                            <p className="text-xs" style={{ color: s.textSub }}>{activeIndex + 1} / {items.length}</p>
                            <Dots items={items} activeIndex={activeIndex} accent={accent} onGo={goTo} />
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Fan: cards en abanico ──
    if (variant === 'fan') {
        const visibleCount = Math.min(items.length, 5);
        const ordered = Array.from({ length: visibleCount }, (_, i) => (activeIndex + i) % items.length);
        const centerIdx = Math.floor(visibleCount / 2);

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="max-w-2xl mx-auto">
                        <div className="relative" style={{ height: cardH + 80 }}>
                            {ordered.map((itemIdx, pos) => {
                                const fanPos = pos - centerIdx;
                                const angle = fanPos * 8;
                                const offsetX = fanPos * 40;
                                const isCenter = pos === centerIdx;
                                const absPos = Math.abs(fanPos);
                                return (
                                    <div key={itemIdx}
                                        ref={isCenter ? measureRef : undefined}
                                        className="absolute left-1/2 rounded-2xl p-6 cursor-pointer select-none"
                                        style={{
                                            width: '90%',
                                            maxWidth: 480,
                                            bottom: 0,
                                            transform: `translateX(calc(-50% + ${offsetX}px)) rotate(${angle}deg) scale(${isCenter ? 1 : 1 - absPos * 0.06})`,
                                            transformOrigin: 'bottom center',
                                            zIndex: visibleCount - absPos,
                                            opacity: isCenter ? 1 : Math.max(0.3, 1 - absPos * 0.25),
                                            backgroundColor: cardBg,
                                            border: `1px solid ${hexToRgba(accent, isCenter ? 0.2 : 0.08)}`,
                                            boxShadow: isCenter ? `0 8px 32px ${hexToRgba(accent, 0.18)}` : `0 2px 12px rgba(0,0,0,0.06)`,
                                            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            pointerEvents: isCenter ? 'auto' : 'none',
                                        }}
                                        onClick={() => goTo(itemIdx)}>
                                        <CardContent item={items[itemIdx]} accent={accent} s={s} />
                                    </div>
                                );
                            })}
                        </div>
                        <Dots items={items} activeIndex={activeIndex} accent={accent} onGo={goTo} />
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Slide: carousel horizontal ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                <div className="max-w-2xl mx-auto">
                    <div className="overflow-hidden rounded-2xl">
                        <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${activeIndex * 100}%)` }}>
                            {items.map((item, i) => (
                                <div key={i} className="w-full shrink-0 p-6 md:p-8 rounded-2xl"
                                    style={{ backgroundColor: cardBg, border: `1px solid ${cardBorder}`, boxShadow: cardShadow }}>
                                    <CardContent item={item} accent={accent} s={s} />
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-6">
                        <button onClick={() => setActiveIndex(i => (i - 1 + items.length) % items.length)}
                            className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
                            style={{ backgroundColor: hexToRgba(accent, 0.1), color: accent }} aria-label="Anterior">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <Dots items={items} activeIndex={activeIndex} accent={accent} onGo={goTo} />
                        <button onClick={advance}
                            className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
                            style={{ backgroundColor: hexToRgba(accent, 0.1), color: accent }} aria-label="Siguiente">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
